// public-frontend/src/components/HumanGate.tsx
/**
 * HumanGate — "I am human" check in front of the marketing home page
 * ─────────────────────────────────────────────────────────────────
 * Twin of frontend/src/components/HumanGate.tsx — same flow, same copy, same
 * backend endpoints; only the `app` tag ('public-frontend', set in
 * utils/humanPass.ts and utils/visitorTracker.ts) differs. Keep them in step.
 *
 * Anonymous visitors to / see a Cloudflare Turnstile checkbox before the
 * page renders. Signed-in users, and anyone holding an unexpired human pass,
 * never see it (App.tsx decides; see utils/humanPass.ts).
 *
 * Flow
 * ----
 *   mount  → visit beacon with gateShown:true          (ledger: humanCheck='gate-shown')
 *   solve  → POST /api/visitors/verify-human (token)   (ledger: 'human-verified' | 'human-failed')
 *   ok     → pass stored, onVerified() → Home renders
 *
 * Turnstile uses the existing site key, which is INVISIBLE in production: most
 * humans have NOTHING to click — Cloudflare checks the browser in the
 * background for a few seconds. Suspicious clients can be escalated to an
 * interactive checkbox, and headless renderers stall here. Admin → Visitors
 * then shows the ones that never got through.
 *
 * What the visitor is told (Oct 5 fix — the first prod visit showed an empty
 * card, then "Try again / Continue anyway", then the home page a second later,
 * with no idea what was being asked):
 *   challenge     → spinner + "Checking your browser… nothing to click"
 *   (> SLOW_HINT_MS) → adds "still checking, a first visit can take longer"
 *   interactive   → "Tick the box below" — ONLY when Cloudflare actually shows
 *                   one (before-interactive-callback), never assumed
 *   verified      → green "You're verified" for VERIFIED_HOLD_MS, then Home,
 *                   so the page change is explained instead of sudden
 * The buttons appear only on a real failure. The false "widget unavailable"
 * that raised them on the first visit was Turnstile.tsx's paint watchdog
 * looking for an iframe an invisible widget does not expose — fixed there.
 *
 * Desktop vs mobile
 * -----------------
 *  • The card is fluid (max-w-md) and vertically centred in the viewport using
 *    dvh, so mobile browser chrome sliding in/out does not push it off-screen.
 *  • Turnstile's normal widget is a fixed 300 px wide. On viewports narrower
 *    than COMPACT_BELOW_PX the card's inner width cannot hold it, so we switch
 *    to the 150×140 compact widget instead of letting it overflow sideways.
 *    The choice is made once on mount — re-rendering the widget mid-challenge
 *    on a rotate would throw away a half-solved token.
 *  • Safe-area insets keep the card clear of notches/home indicators.
 *
 * Search engines
 * --------------
 * A browser whose UA claims to be Googlebot/Bingbot first asks the server
 * (GET /api/visitors/gate-status). Only a crawler verified by reverse+forward
 * DNS gets bypass:true and goes straight to Home — the same page a human sees
 * after the checkbox, so this is not cloaking. A forged crawler UA gets the
 * normal gate. Nothing is painted while that check runs, so a crawler's
 * snapshot never captures the checkbox card.
 *
 * No login is involved: the gate is the checkbox only, and the home page is
 * public. (A "Sign in" shortcut was removed on purpose — it read as if the
 * home page required an account.)
 *
 * Failure policy
 * --------------
 * A gate that can lock real people out of the front door is worse than no
 * gate. If the widget itself cannot load (ad-blocker, Cloudflare unreachable)
 * the visitor gets "Try again" AND "Continue anyway"; continuing is logged as
 * 'gate-skipped' so the admin still sees it. A REJECTED challenge offers only
 * "Try again" — that is the gate doing its job.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShieldCheck, Loader2, RefreshCw, CheckCircle2 } from 'lucide-react';
import Turnstile from './Turnstile';
import { checkCrawlerBypass, claimsSearchCrawler, verifyHuman } from '../utils/humanPass';
import { reportGateSkipped, sessionId, trackHomePageVisit } from '../utils/visitorTracker';
import { log } from '../utils/logger';

const COMPONENT = 'HumanGate';

const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

/** Below this viewport width the 300 px normal widget does not fit the card. */
const COMPACT_BELOW_PX = 360;

/** After this long without a verdict, reassure the visitor it is still working. */
const SLOW_HINT_MS = 8_000;

/** How long the "You're verified" confirmation stays up before Home renders. */
const VERIFIED_HOLD_MS = 900;

type Phase =
  | 'crawler-check'
  | 'challenge'
  | 'verifying'
  | 'verified'
  | 'rejected'
  | 'widget-error'
  | 'network-error';

interface HumanGateProps {
  dark: boolean;
  onVerified: () => void;
}

const HumanGate: React.FC<HumanGateProps> = ({ dark, onVerified }) => {
  const [phase, setPhase] = useState<Phase>(() => (claimsSearchCrawler() ? 'crawler-check' : 'challenge'));
  const [resetKey, setResetKey] = useState(0);
  /** Cloudflare is showing a checkbox the visitor must tick. */
  const [interactive, setInteractive] = useState(false);
  /** The current challenge has run longer than SLOW_HINT_MS. */
  const [slow, setSlow] = useState(false);

  const widgetSize = useMemo<'normal' | 'compact'>(
    () => (typeof window !== 'undefined' && window.innerWidth < COMPACT_BELOW_PX ? 'compact' : 'normal'),
    [],
  );

  // App passes an inline arrow; a ref keeps the crawler check from restarting
  // on every parent render.
  const onVerifiedRef = useRef(onVerified);
  useEffect(() => {
    onVerifiedRef.current = onVerified;
  }, [onVerified]);

  // Claimed search crawler → let the server decide before painting anything.
  useEffect(() => {
    if (phase !== 'crawler-check') return;
    let cancelled = false;
    void checkCrawlerBypass().then((bypass) => {
      if (cancelled) return;
      if (bypass) onVerifiedRef.current();
      else setPhase('challenge');
    });
    return () => {
      cancelled = true;
    };
  }, [phase]);

  // One beacon per tab session, once the gate is really on screen; this is the
  // row the gate outcome joins to. (A verified crawler's beacon fires from Home.)
  const gateOnScreen = phase !== 'crawler-check';
  useEffect(() => {
    if (gateOnScreen) trackHomePageVisit({ gateShown: true });
  }, [gateOnScreen]);

  // "Still checking…" hint for a challenge that runs long. Restarts per attempt.
  useEffect(() => {
    if (phase !== 'challenge') return;
    setSlow(false);
    const t = setTimeout(() => setSlow(true), SLOW_HINT_MS);
    return () => clearTimeout(t);
  }, [phase, resetKey]);

  // Show the confirmation briefly, then open Home.
  useEffect(() => {
    if (phase !== 'verified') return;
    const t = setTimeout(() => onVerifiedRef.current(), VERIFIED_HOLD_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const handleToken = useCallback(
    async (token: string) => {
      setInteractive(false);
      setPhase('verifying');
      const result = await verifyHuman(token, {
        sessionId: sessionId(),
        path: window.location.pathname + window.location.hash,
      });
      if (result.ok) {
        setPhase('verified');
        return;
      }
      log.warn(COMPONENT, 'Human verification did not pass', { reason: result.reason });
      setPhase(result.reason === 'network' ? 'network-error' : 'rejected');
    },
    [],
  );

  const retry = useCallback(() => {
    setInteractive(false);
    setPhase('challenge');
    // Tokens are single-use; a spent one must be replaced by a fresh challenge.
    setResetKey((k) => k + 1);
  }, []);

  const continueAnyway = useCallback(() => {
    reportGateSkipped();
    onVerified();
  }, [onVerified]);

  const muted = dark ? 'text-gray-400' : 'text-gray-500';
  if (phase === 'crawler-check') return null;
  const showWidget = phase === 'challenge' || phase === 'verifying' || phase === 'widget-error';

  return (
    <div
      className="flex items-center justify-center px-4 py-8"
      style={{
        minHeight: 'calc(100dvh - 4rem)', // viewport minus the navbar (App's <main> already wraps this)
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
        paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
      }}
    >
      <section
        aria-labelledby="human-gate-title"
        className={`w-full max-w-md rounded-2xl border p-6 sm:p-8 shadow-xl ${
          dark ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex flex-col items-center text-center">
          <div
            className={`mb-4 flex h-14 w-14 items-center justify-center rounded-full ${
              dark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-600'
            }`}
          >
            <ShieldCheck className="h-7 w-7" aria-hidden="true" />
          </div>
          <h1
            id="human-gate-title"
            className={`text-xl sm:text-2xl font-semibold ${dark ? 'text-gray-100' : 'text-gray-900'}`}
          >
            Quick check before you continue
          </h1>
          <p className={`mt-2 text-sm leading-relaxed ${muted}`}>
            {interactive
              ? 'Tick the box below to confirm you’re human.'
              : 'We’re checking that you’re a person, not a bot. Usually there’s nothing to click — this takes a few seconds.'}
            {' '}This device won&apos;t be asked again for a week.
          </p>

          {showWidget && (
            <div className="mt-6 flex w-full justify-center" aria-busy={phase === 'verifying'}>
              <Turnstile
                siteKey={TURNSTILE_SITE_KEY}
                action="home-gate"
                theme={dark ? 'dark' : 'light'}
                size={widgetSize}
                resetKey={resetKey}
                onSuccess={handleToken}
                onError={() => setPhase('rejected')}
                onUnavailable={() => setPhase('widget-error')}
                onInteractive={() => setInteractive(true)}
                onInteractiveDone={() => setInteractive(false)}
                onExpire={() => setPhase('challenge')}
              />
            </div>
          )}

          <div className="mt-4 min-h-[1.5rem] text-sm" role="status" aria-live="polite">
            {phase === 'challenge' && !interactive && (
              <span className={`inline-flex flex-col items-center gap-1 ${muted}`}>
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Checking your browser…
                </span>
                {slow && <span className="text-xs">Still checking — a first visit can take a little longer.</span>}
              </span>
            )}
            {phase === 'verified' && (
              <span className={`inline-flex items-center gap-2 font-medium ${dark ? 'text-green-300' : 'text-green-700'}`}>
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                You&apos;re verified — opening StockSignals Pro…
              </span>
            )}
            {phase === 'verifying' && (
              <span className={`inline-flex items-center gap-2 ${muted}`}>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Verifying…
              </span>
            )}
            {phase === 'rejected' && (
              <span className={dark ? 'text-red-300' : 'text-red-600'}>
                We couldn&apos;t confirm that one. Please try again.
              </span>
            )}
            {phase === 'network-error' && (
              <span className={dark ? 'text-red-300' : 'text-red-600'}>
                Couldn&apos;t reach the server. Check your connection and try again.
              </span>
            )}
            {phase === 'widget-error' && (
              <span className={muted}>
                Having trouble with the check? Try again, or continue without it.
              </span>
            )}
          </div>

          {(phase === 'rejected' || phase === 'network-error' || phase === 'widget-error') && (
            <div className="mt-4 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
              <button
                type="button"
                onClick={retry}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 text-sm font-medium text-white hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Try again
              </button>
              {phase === 'widget-error' && (
                <button
                  type="button"
                  onClick={continueAnyway}
                  className={`inline-flex min-h-[44px] items-center justify-center rounded-lg border px-5 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
                    dark
                      ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Continue anyway
                </button>
              )}
            </div>
          )}

          <p className={`mt-6 text-xs ${muted}`}>Protected by Cloudflare Turnstile</p>
        </div>
      </section>
    </div>
  );
};

export default HumanGate;
